import { Controller } from "@hotwired/stimulus"

/**
 * TaxonomyTreeController handles expand/collapse of taxonomy tree nodes
 * and debounced search functionality for the taxonomy admin UI.
 *
 * Each tree node has a toggle button that lazy-loads children via Turbo Frames
 * on first expand. Subsequent toggles simply show/hide the already-loaded children.
 *
 * Search input debounces requests and replaces the tree view with search results.
 */
export default class extends Controller {
  static targets = ["searchInput", "treeContainer", "searchResults"]
  static values = {
    debounceMs: { type: Number, default: 300 }
  }

  declare searchInputTarget: HTMLInputElement
  declare treeContainerTarget: HTMLElement
  declare searchResultsTarget: HTMLElement
  declare hasSearchInputTarget: boolean
  declare hasTreeContainerTarget: boolean
  declare hasSearchResultsTarget: boolean
  declare debounceMsValue: number

  private searchTimeout: ReturnType<typeof setTimeout> | null = null

  connect() {
    // Controller connected - tree is ready
  }

  disconnect() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = null
    }
  }

  /**
   * Toggle expand/collapse of a tree node.
   * On first expand, triggers the Turbo Frame to load children.
   * On subsequent toggles, shows/hides the children container.
   */
  toggle(event: Event) {
    const button = (event.currentTarget as HTMLElement)
    const nodeContainer = button.closest("[data-taxonomy-node]") as HTMLElement
    if (!nodeContainer) return

    const childrenContainer = nodeContainer.querySelector("[data-children-container]") as HTMLElement
    if (!childrenContainer) return

    const isExpanded = nodeContainer.getAttribute("data-expanded") === "true"
    const chevronRight = button.querySelector("[data-icon='chevron-right']") as HTMLElement
    const chevronDown = button.querySelector("[data-icon='chevron-down']") as HTMLElement
    const spinner = button.querySelector("[data-icon='spinner']") as HTMLElement

    if (isExpanded) {
      // Collapse
      childrenContainer.classList.add("hidden")
      nodeContainer.setAttribute("data-expanded", "false")
      if (chevronRight) chevronRight.classList.remove("hidden")
      if (chevronDown) chevronDown.classList.add("hidden")
    } else {
      // Expand
      childrenContainer.classList.remove("hidden")
      nodeContainer.setAttribute("data-expanded", "true")
      if (chevronRight) chevronRight.classList.add("hidden")
      if (chevronDown) chevronDown.classList.remove("hidden")

      // Show spinner while turbo frame loads (first time only)
      const turboFrame = childrenContainer.querySelector("turbo-frame") as HTMLElement
      if (turboFrame && !turboFrame.hasAttribute("complete")) {
        if (spinner) spinner.classList.remove("hidden")
        if (chevronDown) chevronDown.classList.add("hidden")

        turboFrame.addEventListener("turbo:frame-load", () => {
          if (spinner) spinner.classList.add("hidden")
          if (chevronDown) chevronDown.classList.remove("hidden")
          turboFrame.setAttribute("complete", "true")
        }, { once: true })
      }
    }
  }

  /**
   * Debounced search handler - waits for user to stop typing before
   * firing the search request via Turbo Frame.
   */
  search() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    this.searchTimeout = setTimeout(() => {
      this.performSearch()
    }, this.debounceMsValue)
  }

  /**
   * Clear search and restore tree view.
   */
  clearSearch() {
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ""
    }
    this.showTree()
  }

  private performSearch() {
    if (!this.hasSearchInputTarget) return

    const query = this.searchInputTarget.value.trim()

    if (query.length === 0) {
      this.showTree()
      return
    }

    this.showSearchResults()

    // The actual search is handled by a Turbo Frame submission
    // triggered by the form's data-turbo-frame attribute
    const form = this.searchInputTarget.closest("form") as HTMLFormElement
    if (form) {
      form.requestSubmit()
    }
  }

  private showTree() {
    if (this.hasTreeContainerTarget) {
      this.treeContainerTarget.classList.remove("hidden")
    }
    if (this.hasSearchResultsTarget) {
      this.searchResultsTarget.classList.add("hidden")
    }
  }

  private showSearchResults() {
    if (this.hasTreeContainerTarget) {
      this.treeContainerTarget.classList.add("hidden")
    }
    if (this.hasSearchResultsTarget) {
      this.searchResultsTarget.classList.remove("hidden")
    }
  }
}
